'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Eye, EyeOff, Mail, Lock, Phone } from 'lucide-react';
import LoginHeader from '@/components/LoginHeader';
import { useAuth } from '@/contexts/AuthContext';
import { IMAGE_NAMES, getImageUrl } from '@/config/imageConstants';
import { authService, LoginWithOtpRequest, VerifyLoginOtpRequest } from '@/services/authService';
import { getErrorMessage } from '@/types/error';
import AuthPageGuard from '@/components/AuthPageGuard';

// Types
interface LoginFormValues {
  username: string;
  password: string;
}

interface OtpFormValues {
  mobile: string;
}

interface OtpVerificationFormValues {
  otp: string;
}

interface ForgotPasswordFormValues {
  mobile: string;
}

interface ChangePasswordFormValues {
  password: string;
  confirmPassword: string;
}

// Validation schemas
const loginSchema = Yup.object().shape({
  username: Yup.string()
    .test('email-or-phone-or-userid', 'Please enter a valid email, phone number, or user ID', function(value) {
      if (!value) return false;
      
      // Check if it's a valid email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(value)) return true;
      
      // Check if it's a valid phone number (10 digits)
      const phoneRegex = /^[0-9]{10}$/;
      if (phoneRegex.test(value)) return true;
      
      // Check if it's a valid user ID (alphanumeric, at least 3 characters)
      const useridRegex = /^[a-zA-Z0-9_]{3,}$/;
      if (useridRegex.test(value)) return true;
      
      return false;
    })
    .required('Email/Phone/User ID is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
});

const otpSchema = Yup.object().shape({
  mobile: Yup.string()
    .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit mobile number')
    .required('Mobile number is required'),
});

const forgotPasswordSchema = Yup.object().shape({
  mobile: Yup.string()
    .matches(/^[0-9]{10}$/, 'Please enter a valid 10-digit mobile number')
    .required('Mobile number is required'),
});

const otpVerificationSchema = Yup.object().shape({
  otp: Yup.string()
    .matches(/^[0-9]{4}$/, 'OTP must be exactly 4 digits')
    .required('OTP is required'),
});

const changePasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

export default function Login() {
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [activeForm, setActiveForm] = useState<'login' | 'otp' | 'otpVerification' | 'forgot' | 'changePassword'>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isForgotPasswordFlow, setIsForgotPasswordFlow] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(true); // Default to mobile to prevent initial load

  // Detect if device is mobile to prevent hero image loading on mobile
  useEffect(() => {
    // Check if window width is greater than 599px (desktop)
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 599);
    };
    
    // Initial check
    checkIsMobile();
    
    // Listen for resize events
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Reset password visibility states when switching forms
  const resetPasswordStates = () => {
    setShowPassword(false);
    setShowNewPassword(false);
  };

  const handleLogin = async (values: LoginFormValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    setIsLoading(true);
    setError(null);
    try {
      // Call the login API using auth service
      const data = await authService.login(values);

      if (data.status === 'success') {
        // Use the AuthContext login function
        await login(data.data.user, data.data.tokens, { method: 'password' });
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (error: unknown) {
      // Login error
      setError(getErrorMessage(error, 'An error occurred during login. Please try again.'));
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleOtpLogin = async (values: OtpFormValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    setIsLoading(true);
    try {
      // Simulate API call to verify OTP for login
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Verifying OTP for login
      // Add your OTP verification logic here
      // After OTP is verified, redirect to dashboard
      // OTP verified successfully, redirecting to dashboard
      // Add your dashboard redirect logic here
      // For now, just show success message
      alert('Login successful! Redirecting to dashboard...');
    } catch (error) {
      // OTP verification error
      alert('OTP verification failed. Please try again.');
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (values: ForgotPasswordFormValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    setIsLoading(true);
    try {
      // Simulate API call to send OTP for password reset
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Sending OTP for password reset
      // Add your OTP sending logic here
      // After OTP is sent, show OTP verification form for password reset
      setMobileNumber(values.mobile);
      setIsForgotPasswordFlow(true);
      setActiveForm('otpVerification');
    } catch (error) {
      // Forgot password error
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (values: ChangePasswordFormValues, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    setIsLoading(true);
    try {
      // Simulate API call to update password
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Updating password for user
      // Add your password update logic here
      // After successful password update, redirect to dashboard
      // Password updated successfully, redirecting to dashboard
      alert('Password updated successfully! Redirecting to dashboard...');
      // Add your dashboard redirect logic here
      setOtpVerified(false);
      setIsForgotPasswordFlow(false);
    } catch (error) {
      // Change password error
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const renderLoginForm = () => (
    <Formik<LoginFormValues>
      key="login-form"
      initialValues={{ username: '', password: '' }}
      validationSchema={loginSchema}
      onSubmit={handleLogin}
    >
      {({ isSubmitting }) => (
        <Form className="space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h2>
            <p className="text-gray-600">Sign in to your account to continue</p>
            {error && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {error}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
              Email/Phone/User ID
            </label>
            <div className="relative">
              <Field
                type="text"
                id="username"
                name="username"
                placeholder="Email/Phone/User ID"
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              />
              <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            <ErrorMessage name="username" component="div" className="mt-1 text-sm text-red-600" />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <Field
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                placeholder="Enter your password"
                className="w-full px-4 py-3 pl-12 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              />
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <ErrorMessage name="password" component="div" className="mt-1 text-sm text-red-600" />
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                resetPasswordStates();
                setActiveForm('forgot');
              }}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              Forgot Password?
            </button>
            <button
              type="button"
              onClick={() => {
                resetPasswordStates();
                setActiveForm('otp');
              }}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              Login with OTP
            </button>
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>

          <div className="text-center">
            <p className="text-gray-600">
              Not registered yet?{' '}
              <Link
                href="/register"
                className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                Register Free
              </Link>
            </p>
          </div>
        </Form>
      )}
    </Formik>
  );

  const renderOtpForm = () => (
    <Formik<OtpFormValues>
      key="otp-form"
      initialValues={{ mobile: '' }}
      validationSchema={otpSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setIsLoading(true);
        setError(null);
        try {
          // Call the login with OTP API
          const loginData: LoginWithOtpRequest = {
            phone: values.mobile
          };
          
          const response = await authService.loginWithOtp(loginData);
          
          if (response.status === 'success') {
            // OTP sent successfully
            setMobileNumber(values.mobile);
            setIsForgotPasswordFlow(false);
            setActiveForm('otpVerification');
          } else {
            setError(response.message || 'Failed to send OTP. Please try again.');
          }
        } catch (error: unknown) {
          // OTP sending error
          setError(getErrorMessage(error, 'Failed to send OTP. Please try again.'));
        } finally {
          setIsLoading(false);
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting }) => (
        <Form className="space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Login with OTP</h2>
            <p className="text-gray-600">Enter your mobile number to receive OTP</p>
            {error && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {error}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">
              Mobile Number
            </label>
            <div className="relative">
              <Field
                type="tel"
                id="mobile"
                name="mobile"
                placeholder="Enter your 10-digit mobile number"
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              />
              <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            <ErrorMessage name="mobile" component="div" className="mt-1 text-sm text-red-600" />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Sending OTP...' : 'Send OTP'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                resetPasswordStates();
                setActiveForm('login');
              }}
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              ← Back to Login
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  const renderForgotPasswordForm = () => (
    <Formik<ForgotPasswordFormValues>
      key="forgot-password-form"
      initialValues={{ mobile: '' }}
      validationSchema={forgotPasswordSchema}
      onSubmit={handleForgotPassword}
    >
      {({ isSubmitting }) => (
        <Form className="space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h2>
            <p className="text-gray-600">Enter your registered mobile number to receive OTP</p>
          </div>

          <div>
            <label htmlFor="forgot-mobile" className="block text-sm font-medium text-gray-700 mb-2">
              Mobile Number
            </label>
            <div className="relative">
              <Field
                type="tel"
                id="forgot-mobile"
                name="mobile"
                placeholder="Enter your 10-digit mobile number"
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              />
              <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            <ErrorMessage name="mobile" component="div" className="mt-1 text-sm text-red-600" />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Sending OTP...' : 'Send OTP'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                resetPasswordStates();
                setActiveForm('login');
              }}
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              ← Back to Login
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  const renderOtpVerificationForm = () => (
    <Formik<OtpVerificationFormValues>
      key="otp-verification-form"
      initialValues={{ otp: '' }}
      validationSchema={otpVerificationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setIsLoading(true);
        setError(null);
        try {
          if (isForgotPasswordFlow) {
            // For forgot password flow, show change password form
            setOtpVerified(true);
            setActiveForm('changePassword');
          } else {
            // For login with OTP flow, verify OTP via API
            const verifyData: VerifyLoginOtpRequest = {
              phone: mobileNumber,
              otp: values.otp
            };
            
            const response = await authService.verifyLoginOtp(verifyData);
            
            if (response.status === 'success') {
              // OTP verified successfully for login
              // Use the AuthContext login function to store tokens and user data
              await login(response.data.user, response.data.tokens, { method: 'otp' });
              // The login function will handle redirect to dashboard
            } else {
              setError(response.message || 'OTP verification failed. Please try again.');
            }
          }
        } catch (error: unknown) {
          // OTP verification error
          setError(getErrorMessage(error, 'OTP verification failed. Please try again.'));
        } finally {
          setIsLoading(false);
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting }) => (
        <Form className="space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Verify OTP</h2>
            <p className="text-gray-600">Enter the OTP sent to {mobileNumber}</p>
            {error && (
              <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {error}
              </div>
            )}
          </div>

          <div>
            <label htmlFor="otp-input" className="block text-sm font-medium text-gray-700 mb-2">
              OTP
            </label>
            <div className="relative">
              <Field
                type="text"
                id="otp-input"
                name="otp"
                placeholder="Enter 4-digit OTP"
                maxLength={4}
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 text-center text-lg tracking-widest"
              />
              <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            <ErrorMessage name="otp" component="div" className="mt-1 text-sm text-red-600" />
          </div>

          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Verifying OTP...' : 'Verify OTP'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                resetPasswordStates();
                setActiveForm('login');
              }}
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              ← Back to Login
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  const renderChangePasswordForm = () => (
    <Formik<ChangePasswordFormValues>
      key="change-password-form"
      initialValues={{ password: '', confirmPassword: '' }}
      validationSchema={changePasswordSchema}
      onSubmit={handleChangePassword}
    >
      {({ isSubmitting }) => (
        <Form className="space-y-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Change Password</h2>
            <p className="text-gray-600">Enter your new password</p>
          </div>

          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <div className="relative">
              <Field
                type={showNewPassword ? 'text' : 'password'}
                id="new-password"
                name="password"
                placeholder="Enter your new password"
                className="w-full px-4 py-3 pl-12 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              />
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <ErrorMessage name="password" component="div" className="mt-1 text-sm text-red-600" />
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <Field
                type={showNewPassword ? 'text' : 'password'}
                id="confirm-password"
                name="confirmPassword"
                placeholder="Confirm your new password"
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
              />
              <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            </div>
            <ErrorMessage name="confirmPassword" component="div" className="mt-1 text-sm text-red-600" />
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="flex-1 bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Updating Password...' : 'Update Password'}
            </button>
            <button
              type="button"
              onClick={() => {
                // Skip password reset, redirecting to dashboard
                alert('Skipping password reset. Redirecting to dashboard...');
                // Add your dashboard redirect logic here
              }}
              className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
            >
              Skip
            </button>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                resetPasswordStates();
                setActiveForm('login');
              }}
              className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              ← Back to Login
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  return (
    <AuthPageGuard>
      <div className="min-h-screen bg-white w-full">
        {/* Transparent Header */}
        <LoginHeader variant="transparent" />
        
        {/* Main Content Section - 100vh with 2 column 50/50 split */}
        <section className="h-screen grid grid-cols-2 max-[599px]:grid-cols-1 w-full">
        {/* Left Side - Full Image (50%) - Only load on desktop to improve mobile performance */}
        {!isMobile && (
          <div className="relative w-full max-[599px]:hidden">
            <div className="absolute inset-0 w-full">
              <Image
                src={getImageUrl(IMAGE_NAMES.BANNER)}
                alt="Marathi Vadhu Var Suchak - Most Trusted Marathi Matrimony Platform for Marathi Brides and Grooms"
                fill
                className="object-cover"
                priority
                unoptimized
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent"></div>
            </div>
          </div>
        )}

        {/* Right Side - Login Form (50%) */}
        <div className="flex items-center justify-center px-8 lg:px-16 max-[599px]:w-full">
          <div className="w-full max-w-lg">
            {activeForm === 'login' && renderLoginForm()}
            {activeForm === 'otp' && renderOtpForm()}
            {activeForm === 'forgot' && renderForgotPasswordForm()}
            {activeForm === 'otpVerification' && renderOtpVerificationForm()}
            {activeForm === 'changePassword' && renderChangePasswordForm()}
          </div>
        </div>
      </section>

      </div>
    </AuthPageGuard>
  );
}
